import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import React from 'react';

import { DividerWithText } from './divider-with-text';
import { useMuiSwitchableTheme } from '../theme';

export type ScrollableTabSectionProps = {
  title: string;
  subtitle?: string;
  scrollRef: React.RefObject<HTMLDivElement | null>;
  sections: {
    title: string;
    subtitle?: string;
    content: React.ReactNode;
    visible?: boolean;
  }[];
};

export type TabbedContainerProps = {
  title: string;
  actions?: React.ReactNode;
  tabs: {
    title: string;
    subtitle?: string;
    icon?: React.ReactNode;
    sections: ScrollableTabSectionProps['sections'];
  }[];
};

export const TabbedContainer: React.FC<TabbedContainerProps> = ({
  title,
  tabs,
  actions,
}) => {
  const [tab, setTab] = React.useState(tabs[0].title);
  const scrollRef = React.useRef<HTMLDivElement>(null);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setTab(newValue);
    scrollRef?.current?.scrollTo({ top: 0, behavior: 'instant' });
  };

  const { marginTop } = useMuiSwitchableTheme();

  return (
    <Card
      sx={{
        width: '100%',
        typography: 'body1',

        height: `calc(100dvh - ${marginTop + 16}px)`,
      }}
      component={Grid}
      elevation={1}
      container
    >
      <TabContext value={tab}>
        <Grid
          sx={{
            borderRight: 1,
            borderColor: 'divider',
            display: { xs: 'none', md: 'block' },
          }}
          size={3}
        >
          <CardHeader title={title} sx={{ textAlign: 'center' }} />
          <Divider />
          <TabList
            onChange={handleChange}
            orientation="vertical"
            variant="scrollable"
            sx={{ bgColor: 'transparent' }}
          >
            {tabs.map((tabItem, index) => (
              <Tab
                key={index}
                label={tabItem.title}
                value={tabItem.title}
                // icon={tabItem.icon}
                iconPosition="start"
                sx={{ justifyContent: 'flex-end' }}
              />
            ))}
          </TabList>
        </Grid>
        <Box
          sx={{
            display: { xs: 'block', md: 'none' },
            p: 2,
            width: '100%',
          }}
        >
          <FormControl fullWidth size="small">
            <InputLabel>{title}</InputLabel>
            <Select
              value={tab}
              label={title}
              onChange={(e) =>
                handleChange(e as React.SyntheticEvent, e.target.value)
              }
            >
              {tabs.map((tabItem) => (
                <MenuItem key={tabItem.title} value={tabItem.title}>
                  {tabItem.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Grid
          size={{ xs: 12, md: 9 }}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: {
              xs: `calc(100dvh - ${marginTop + 80}px)`,
              md: `calc(100dvh - ${marginTop + 28}px)`,
            },
          }}
        >
          <Box
            sx={{
              overflowY: 'scroll',
              flex: 1,

              backgroundColor: 'background.paper',
              // Add a gentle fade effect at the bottom
              WebkitMask: 'linear-gradient(0deg, #0000, #000 5% 95%, #0000)',
              scrollbarWidth: 'none',
            }}
            ref={scrollRef}
          >
            {tabs.map((tabItem) => (
              <TabPanel key={tabItem.title} value={tabItem.title} keepMounted>
                <ScrollableTabSection {...tabItem} scrollRef={scrollRef} />
              </TabPanel>
            ))}
          </Box>
          <CardActions
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              height: 80,
              borderTop: 1,
              borderColor: 'divider',
              p: { xs: 1, sm: 2 },
            }}
          >
            <Box sx={{ flexGrow: 1 }} />
            {actions && <Box sx={{ display: 'flex', gap: 1 }}>{actions}</Box>}
          </CardActions>
        </Grid>
      </TabContext>
    </Card>
  );
};

export const ScrollableTabSection: React.FC<ScrollableTabSectionProps> = ({
  title,
  sections,
  scrollRef,
  subtitle = '',
}) => {
  const [tab, setTab] = React.useState(0);
  const sectionRefs = React.useRef<(HTMLDivElement | null)[]>([]);

  const handleChange = (_e: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
    const section = sectionRefs.current[newValue];

    if (section && scrollRef.current) {
      const newOffsetTop = section.offsetTop - scrollRef.current.offsetTop - 60; // Adjust for any fixed header or padding
      scrollRef.current.scrollTo({
        // If newValue is 0, scroll to the top
        top: newValue === 0 ? 0 : newOffsetTop,
        behavior: 'smooth',
      });
    }
  };

  return (
    <>
      <CardHeader title={title} subheader={subtitle} />
      <Tabs
        onChange={handleChange}
        value={tab}
        variant="scrollable"
        allowScrollButtonsMobile
        scrollButtons="auto"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          display: sections.length > 1 ? 'flex' : 'none',
        }}
      >
        {sections.map(({ title, visible = true }, idx) => (
          <Tab
            key={idx}
            label={title}
            value={idx}
            // icon={tabItem.icon}
            iconPosition="start"
            sx={visible ? {} : { display: 'none' }}
          />
        ))}
      </Tabs>
      <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
        {sections.map(({ visible = true, ...section }, index) => (
          <Box
            key={index}
            sx={{ mb: 3, mt: 1, display: visible ? 'block' : 'none' }}
            ref={(el) => {
              sectionRefs.current[index] = el as HTMLDivElement | null;
            }}
          >
            <DividerWithText text={section.title} align="left" />
            {!!section.subtitle && (
              <Typography
                variant="subtitle2"
                color="textSecondary"
                display="block"
                sx={{ mx: 1, mb: 2 }}
              >
                {section.subtitle}
              </Typography>
            )}
            {section.content}
          </Box>
        ))}
      </CardContent>
    </>
  );
};
